import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  fetchEvaluationsFailure,
  fetchEvaluationsSuccess,
  fetchResidentEvaluationsFailure,
  fetchResidentEvaluationsSuccess,
  fetchEvaluationFailure,
  fetchEvaluationSuccess,
  saveEvaluationFailure,
  saveEvaluationSuccess,
  updateEvaluationFailure,
  updateEvaluationSuccess,
  updatePlanEvaluationFailure,
  updatePlanEvaluationSuccess,
  updatePlanResponsibleFailure,
  updatePlanResponsibleSuccess,
} from "./actions";
import {
  FETCH_EVALUATIONS,
  FETCH_RESIDENT_EVALUATIONS,
  FETCH_EVALUATION,
  SAVE_EVALUATION,
  UPDATE_EVALUATION,
  UPDATE_PLAN_EVALUATION,
  UPDATE_PLAN_RESPONSIBLE,
} from "./constants";

export function* fetchEvaluationsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, params } = payload;
  const query = QueryString.stringify(params);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/care-plan-evaluations/${id}?${query}`,
    );
    yield put(fetchEvaluationsSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchEvaluationsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching evaluations!"));
  }
}

export function* fetchResidentEvaluationsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, params } = payload;
  const query = QueryString.stringify(params || {});
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${residentId}/care-plan-evaluations?${query}`,
    );
    yield put(fetchResidentEvaluationsSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    // Tolerated quietly — the endpoint 404s until dev-api redeploys develop;
    // the drawer shows its empty state instead of a toast loop.
    yield put(fetchResidentEvaluationsFailure());
  }
}

export function* fetchEvaluationSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id = payload;
  try {
    const { data } = yield call(
      api(token).get,
      `/api/care-plan-evaluation/${id}`,
    );
    yield put(fetchEvaluationSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchEvaluationFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching evaluation!"));
  }
}

export function* saveEvaluationSaga({ payload, onSave }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const data = yield call(
      api(token).post,
      `/api/care-plan-evaluation/save`,
      payload,
    );
    if (data) {
      yield put(successAlert("Evaluation saved successfully!"));
      yield put(saveEvaluationSuccess(data));
      typeof onSave === "function" && onSave(data);
    }
  } catch (e) {
    yield put(saveEvaluationFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving evaluation!"));
  }
}

export function* updateEvaluationSaga({ payload, onUpdate }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, formData } = payload;
  try {
    const data = yield call(
      api(token).post,
      `/api/care-plan-evaluation/update/${id}`,
      formData,
    );
    if (data) {
      yield put(successAlert("Evaluation updated successfully!"));
      yield put(updateEvaluationSuccess(data));
      typeof onUpdate === "function" && onUpdate(data);
    }
  } catch (e) {
    yield put(updateEvaluationFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating evaluation!"));
  }
}

export function* updatePlanEvaluationSaga({ payload, onUpdate }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, formData } = payload;
  try {
    const data = yield call(
      api(token).post,
      `/api/resident-care-plan/${id}/next-evaluation-date`,
      formData,
    );
    if (data) {
      yield put(successAlert("Plan evaluation updated successfully!"));
      yield put(updatePlanEvaluationSuccess(data));
      typeof onUpdate === "function" && onUpdate(data);
    }
  } catch (e) {
    yield put(updatePlanEvaluationFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating plan evaluation!"));
  }
}

export function* updatePlanResponsibleSaga({ payload, onUpdate }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, formData } = payload;
  try {
    const data = yield call(
      api(token).post,
      `/api/resident-care-plan/${id}/responsible-employee`,
      formData,
    );
    if (data) {
      yield put(successAlert("Responsible staff member updated!"));
      yield put(updatePlanResponsibleSuccess(data));
      typeof onUpdate === "function" && onUpdate(data);
    }
  } catch (e) {
    yield put(updatePlanResponsibleFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating responsible staff member!"));
  }
}

export function* fetchEvaluationsFlow() {
  yield takeEvery(FETCH_EVALUATIONS, fetchEvaluationsSaga);
}
export function* fetchResidentEvaluationsFlow() {
  yield takeEvery(FETCH_RESIDENT_EVALUATIONS, fetchResidentEvaluationsSaga);
}
export function* fetchEvaluationFlow() {
  yield takeEvery(FETCH_EVALUATION, fetchEvaluationSaga);
}
export function* saveEvaluationFlow() {
  yield takeEvery(SAVE_EVALUATION, saveEvaluationSaga);
}
export function* updateEvaluationFlow() {
  yield takeEvery(UPDATE_EVALUATION, updateEvaluationSaga);
}

export function* updatePlanEvaluationFlow() {
  yield takeEvery(UPDATE_PLAN_EVALUATION, updatePlanEvaluationSaga);
}
export function* updatePlanResponsibleFlow() {
  yield takeEvery(UPDATE_PLAN_RESPONSIBLE, updatePlanResponsibleSaga);
}
export default function* ResidentEvaluationSaga() {
  yield all([
    fetchEvaluationsFlow(),
    fetchResidentEvaluationsFlow(),
    fetchEvaluationFlow(),
    saveEvaluationFlow(),
    updateEvaluationFlow(),
    updatePlanEvaluationFlow(),
    updatePlanResponsibleFlow(),
  ]);
}
